import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import type { EmployeeRole } from './permissions';

/**
 * Resolves the current employee's effective role.
 *
 * Seller-only logins (no employee record) fall back to 'owner' — the bridge
 * (Phase 11) auto-mints them as owners. Returns null only when not
 * authenticated through any portal we recognize.
 *
 * Auth detection order:
 *   1. Agency Bearer token (Phase 1-D `state.auth.agency.accessToken`)
 *   2. Legacy seller token (`state.auth.authorization.accessToken.sellerToken`)
 *
 * If neither is set we treat the user as logged out and return null.
 */
export function useEmployeeRole(): EmployeeRole | null {
  const identity = useSelector(
    (s: RootState) => s.auth?.agency?.employeeIdentity
  );
  const isAuthenticated = useSelector((s: RootState) =>
    Boolean(
      s.auth?.agency?.accessToken ||
        s.auth?.authorization?.accessToken?.sellerToken
    )
  );

  if (!isAuthenticated) return null;
  // Seller-only logins are auto-bridged as owners. The bridge (Phase 11) sets
  // employeeIdentity for every successful login, but defensive default handles
  // the edge case where the /me call hasn't resolved yet.
  return ((identity?.role as EmployeeRole | undefined) ?? 'owner') as EmployeeRole;
}
